import { useState, useEffect } from 'react';
import { FiPlus, FiUsers, FiGrid, FiStar, FiEye, FiDownload } from 'react-icons/fi';
import { exportToExcel } from '../../utils/exportUtils';
import api from '../../utils/api';
import DataTable from '../../components/DataTable';
import Modal from '../../components/Modal';
import Badge from '../../components/Badge';
import { formatDate } from '../../utils/dateHelpers';

const ACTIVITY_TYPES = ['Lecture', 'Event', 'Workshop'];
const TYPE_COLORS = { Lecture: 'valid', Event: 'planned', Workshop: 'expiring' };

export default function GrandRounds() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editModal, setEditModal] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [qrModal, setQrModal] = useState(null);
  const [attendeesModal, setAttendeesModal] = useState(null);
  const [satisfactionModal, setSatisfactionModal] = useState(null);

  const fetchItems = async () => {
    setLoading(true);
    try {
      const data = await api.get('/grand-rounds');
      setItems(data.grand_rounds || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchItems(); }, []);

  const handleSave = async (formData) => {
    try {
      if (editItem?.id) {
        await api.put(`/grand-rounds/${editItem.id}`, formData);
      } else {
        await api.post('/grand-rounds', formData);
      }
      setEditModal(false);
      setEditItem(null);
      fetchItems();
    } catch (err) {
      alert(err.message);
    }
  };

  const handleDelete = async (row) => {
    if (!confirm(`Delete "${row.topic_name}"?`)) return;
    try {
      await api.delete(`/grand-rounds/${row.id}`);
      fetchItems();
    } catch (err) {
      alert(err.message);
    }
  };

  const generateQR = async (id, type) => {
    try {
      const data = await api.post(`/grand-rounds/${id}/generate-${type}-qr`);
      setQrModal({ type, data });
    } catch (err) {
      alert(err.message);
    }
  };

  const viewAttendees = async (id) => {
    try {
      const data = await api.get(`/grand-rounds/${id}/attendees`);
      setAttendeesModal(data);
    } catch (err) {
      alert(err.message);
    }
  };

  const viewSatisfaction = async (id) => {
    try {
      const data = await api.get(`/grand-rounds/${id}/satisfaction`);
      setSatisfactionModal(data);
    } catch (err) {
      alert(err.message);
    }
  };

  const downloadQR = (dataUrl, filename) => {
    const link = document.createElement('a');
    link.href = dataUrl;
    link.download = filename + '.png';
    link.click();
  };

  const columns = [
    { key: 'activity_type', label: 'Type', render: (v) => <Badge status={TYPE_COLORS[v]} text={v} /> },
    { key: 'topic_name', label: 'Topic' },
    { key: 'presenter', label: 'Presenter' },
    { key: 'date', label: 'Date', render: (v) => formatDate(v) },
    { key: 'attendee_count', label: 'Attendees' },
    {
      key: 'actions_qr', label: 'QR / Details',
      render: (_, row) => (
        <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
          <button className="btn btn-sm btn-secondary" onClick={() => generateQR(row.id, 'attendance')}><FiGrid size={12} /> Attend</button>
          <button className="btn btn-sm btn-secondary" onClick={() => generateQR(row.id, 'satisfaction')}><FiStar size={12} /> Survey</button>
          <button className="btn btn-sm btn-secondary" onClick={() => viewAttendees(row.id)}><FiUsers size={12} /></button>
          <button className="btn btn-sm btn-secondary" onClick={() => viewSatisfaction(row.id)}><FiEye size={12} /></button>
        </div>
      ),
    },
  ];

  return (
    <div>
      <div className="card" style={{ marginBottom: '20px' }}>
        <div className="card-header">
          <h3>Grand Round Activities</h3>
          <button className="btn btn-primary btn-sm" onClick={() => { setEditItem(null); setEditModal(true); }}>
            <FiPlus size={14} /> Add Activity
          </button>
        </div>
      </div>

      {loading ? (
        <div className="card text-center p-4"><div className="spinner" style={{ margin: '40px auto' }} /></div>
      ) : (
        <DataTable columns={columns} data={items} onEdit={(row) => { setEditItem(row); setEditModal(true); }} onDelete={handleDelete} />
      )}

      <Modal isOpen={editModal} onClose={() => { setEditModal(false); setEditItem(null); }} title={editItem ? 'Edit Grand Round' : 'Add Grand Round'}>
        <GrandRoundForm item={editItem} onSave={handleSave} onCancel={() => { setEditModal(false); setEditItem(null); }} />
      </Modal>

      <Modal isOpen={!!qrModal} onClose={() => setQrModal(null)} title={`${qrModal?.type === 'attendance' ? 'Attendance' : 'Satisfaction'} QR Code`}>
        {qrModal && (
          <div style={{ textAlign: 'center' }}>
            <img src={qrModal.data.qr_data_url} alt="QR Code" style={{ maxWidth: '300px', margin: '0 auto 16px' }} />
            <p className="text-sm text-muted mb-3">Scan to submit {qrModal.type}</p>
            <button className="btn btn-primary btn-sm" onClick={() => downloadQR(qrModal.data.qr_data_url, `gr_${qrModal.type}_qr`)}>Download PNG</button>
          </div>
        )}
      </Modal>

      <Modal isOpen={!!attendeesModal} onClose={() => setAttendeesModal(null)} title={`Attendees (${attendeesModal?.count || 0})`}>
        {attendeesModal && (
          attendeesModal.attendees.length === 0 ? (
            <p className="text-muted text-center p-3">No attendees yet.</p>
          ) : (
            <>
              <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '12px' }}>
                <button
                  className="btn btn-sm btn-primary"
                  onClick={() => {
                    const rows = attendeesModal.attendees.map((a) => ({
                      'Employee ID': a.employee_id,
                      'Name': a.staff_name,
                      'Department': a.department,
                      'Scanned At': a.scanned_at,
                    }));
                    exportToExcel(rows, `grand_round_attendees_${attendeesModal.attendees.length}`);
                  }}
                  style={{ display: 'flex', alignItems: 'center', gap: '4px' }}
                >
                  <FiDownload size={13} /> Export Excel
                </button>
              </div>
              <div style={{ maxHeight: '400px', overflow: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead><tr><th style={thStyle}>Employee ID</th><th style={thStyle}>Name</th><th style={thStyle}>Department</th><th style={thStyle}>Time</th></tr></thead>
                  <tbody>
                    {attendeesModal.attendees.map((a) => (
                      <tr key={a.id}><td style={tdStyle}>{a.employee_id}</td><td style={tdStyle}>{a.staff_name}</td><td style={tdStyle}>{a.department}</td><td style={tdStyle}>{formatDate(a.scanned_at)}</td></tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )
        )}
      </Modal>

      <Modal isOpen={!!satisfactionModal} onClose={() => setSatisfactionModal(null)} title={`Satisfaction (${satisfactionModal?.count || 0} responses)`}>
        {satisfactionModal && satisfactionModal.count > 0 ? (
          <div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '16px' }}>
              {[['Overall Quality', satisfactionModal.averages.q1], ['Relevance', satisfactionModal.averages.q2], ['Delivery', satisfactionModal.averages.q3], ['Organization', satisfactionModal.averages.q4]].map(([l, v]) => (
                <div key={l} style={{ padding: '12px', backgroundColor: '#F5F0E8', borderRadius: '8px', textAlign: 'center' }}>
                  <div style={{ fontSize: '1.25rem', fontWeight: 700, color: '#2B362E' }}>{v}/5</div>
                  <div style={{ fontSize: '0.75rem', color: '#6b6b6b' }}>{l}</div>
                </div>
              ))}
            </div>
            <div style={{ textAlign: 'center', padding: '12px', backgroundColor: '#e8f0e5', borderRadius: '8px' }}>
              <span className="text-sm font-medium">{satisfactionModal.averages.recommend_pct}% would recommend</span>
            </div>
          </div>
        ) : satisfactionModal ? (
          <p className="text-muted text-center p-3">No responses yet.</p>
        ) : null}
      </Modal>
    </div>
  );
}

function GrandRoundForm({ item, onSave, onCancel }) {
  const [form, setForm] = useState({
    activity_type: item?.activity_type || 'Lecture',
    topic_name: item?.topic_name || '',
    presenter: item?.presenter || '',
    date: item?.date || '',
  });
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  return (
    <form onSubmit={(e) => { e.preventDefault(); onSave(form); }}>
      <div className="form-row">
        <div className="form-group">
          <label>Activity Type *</label>
          <select value={form.activity_type} onChange={(e) => set('activity_type', e.target.value)}>
            {ACTIVITY_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
        </div>
        <div className="form-group">
          <label>Date *</label>
          <input type="date" value={form.date} onChange={(e) => set('date', e.target.value)} required />
        </div>
      </div>
      <div className="form-group"><label>Topic Name *</label><input value={form.topic_name} onChange={(e) => set('topic_name', e.target.value)} required /></div>
      <div className="form-group"><label>Presenter</label><input value={form.presenter} onChange={(e) => set('presenter', e.target.value)} /></div>
      <div style={{ display: 'flex', gap: '10px', marginTop: '20px', justifyContent: 'flex-end' }}>
        <button type="button" className="btn btn-secondary" onClick={onCancel}>Cancel</button>
        <button type="submit" className="btn btn-primary">Save</button>
      </div>
    </form>
  );
}

const thStyle = { padding: '10px 12px', fontSize: '0.8rem', fontWeight: 600, textAlign: 'left', borderBottom: '2px solid #f0ece4', color: '#2B362E' };
const tdStyle = { padding: '10px 12px', fontSize: '0.85rem', borderBottom: '1px solid #f0ece4' };
